import { useTranslation } from 'react-i18next';
import { SiInstagram, SiTiktok, SiYoutube } from 'react-icons/si';
import { formatCents } from '@/lib/format';
import type { CpmRatePlatform } from '@/lib/campaigns/describeCampaignCpmRates';
import { cn } from '@/lib/utils';

const ICONS: Record<CpmRatePlatform, typeof SiInstagram> = {
  instagram: SiInstagram,
  tiktok: SiTiktok,
  youtube: SiYoutube,
};

type Props = {
  rates: { platform: CpmRatePlatform; rateCents: number }[];
  currency: string;
  /** `inline` (default) separates networks with dots; `stacked` puts one per line. */
  layout?: 'inline' | 'stacked';
  className?: string;
};

/** Each network's CPM, as a compact inline list: icon + rate, separated by dots. */
export function CampaignCpmRateList({
  rates, currency, layout = 'inline', className,
}: Props) {
  const { t } = useTranslation();
  if (rates.length === 0) return null;
  return (
    <ul
      data-testid="campaign-cpm-rate-list"
      className={cn(
        layout === 'stacked'
          ? 'flex flex-col gap-0.5 text-xs text-muted-foreground'
          : 'flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground',
        className,
      )}
    >
      {rates.map(({ platform, rateCents }, index) => {
        const Icon = ICONS[platform];
        const amount = formatCents(rateCents, currency);
        return (
          <li
            key={platform}
            data-testid={`campaign-cpm-rate-${platform}`}
            aria-label={`${t(`campaign.channels.platformLabel.${platform}`)}: ${amount}`}
            className="inline-flex items-center gap-1 whitespace-nowrap"
          >
            {layout === 'inline' && index > 0 && (
              <span aria-hidden="true" className="text-muted-foreground/60">·</span>
            )}
            <Icon aria-hidden="true" className="h-3 w-3 shrink-0" />
            <span className="font-semibold text-foreground">{amount}</span>
          </li>
        );
      })}
    </ul>
  );
}
